/**
 * CR-7277 — whether a PO line actually has a change to accept or reject.
 *
 * The screens used to infer this from the presence of three *display* values,
 * which is how an empty state came to present as a change request: a line
 * whose `acknowledge_status` said `change_proposed_*` while every
 * `supplier_proposed_*` field was empty rendered "A change has been proposed"
 * next to live Accept and Reject buttons. Accepting there does real damage —
 * the accept path falls through to `supplier_promise_date` and writes it over
 * the line's agreed date, which re-anchors OTIF and the supplier's scorecard.
 *
 * So the question "is there something to act on?" gets one answer, used by
 * every surface that offers an action, rather than each one re-deriving it.
 *
 * Note the deliberate difference from what the UI *renders*: a supplier who
 * proposes the date the line already carries writes a real payload that the
 * screen has nothing to show for. That is a payload (so the status is not
 * stale) but not a change (so there is nothing to display or agree). The two
 * predicates below keep those apart.
 */

/** The subset of a PO line this module needs. */
export interface ProposalFields {
  acknowledge_status?: string | null;
  supplier_proposed_date?: string | null;
  supplier_proposed_qty?: string | number | null;
  supplier_proposed_unit_price?: string | number | null;
  original_promise_date?: string | null;
  delivery_date?: string | null;
}

/** Statuses that claim a change is pending. */
export const CHANGE_PROPOSED_STATUSES = [
  'change_proposed_supplier',
  'change_proposed_customer',
] as const;

export function isChangeProposedStatus(status?: string | null): boolean {
  return !!status && (CHANGE_PROPOSED_STATUSES as readonly string[]).includes(status);
}

/**
 * Customer review 11 Sep (item 28): may a screen present this line as
 * carrying an open change request at all?
 *
 * Only the workflow status can say so. Every propose path in
 * POLineWorkflowService (propose_alternative_date / propose_quantity_change /
 * propose_price_change) writes `change_proposed_*` in the same save as the
 * proposed value, and every accept / reject / cancel clears the value before
 * it resolves the status. A genuine proposal therefore always carries the
 * status. A `supplier_proposed_*` value on a line in any other status is
 * residue: on ERP-import tenants it is the legacy `SupplierProposeDate`
 * column, populated on 94,055 of the reviewed tenant's 96,097 lines, all of them `new` or
 * `wip` -- which is how every line came to show "Active Change Request, Open,
 * Awaiting Decision, Unknown" with Accept / Reject.
 *
 * A `new` line with nothing proposed is still awaiting the supplier's
 * acknowledgement (accept_delivery_date's initial accept), so the header's
 * "Waiting on Supplier" stays -- that is an acknowledgement owed, not a change
 * to decide. The CR-4882 / CR-7277 guards below still govern a
 * `change_proposed_*` row whose payload is missing or a no-op.
 *
 * @param line - The line's workflow fields.
 * @returns True when the status says a change is pending.
 */
export function hasOpenChangeRequest(line: ProposalFields): boolean {
  return isChangeProposedStatus(line.acknowledge_status);
}

/**
 * Does the line carry any proposed value at all?
 *
 * This is the safety question. It ignores whether the value differs from what
 * the line already holds — a same-date proposal still counts, because the
 * status is then backed by something the user really sent.
 */
export function hasProposalPayload(line: ProposalFields): boolean {
  return Boolean(
    line.supplier_proposed_date ||
    line.supplier_proposed_qty ||
    line.supplier_proposed_unit_price,
  );
}

/**
 * A line whose status claims a pending change but which carries no proposed
 * value to back it. Nothing may act on one of these.
 */
export function isPhantomChangeRequest(line: ProposalFields): boolean {
  return isChangeProposedStatus(line.acknowledge_status) && !hasProposalPayload(line);
}

/**
 * A payload that exists but proposes the date the line already has, so there
 * is nothing to show and nothing to agree. Distinguished from a phantom
 * because the cause and the wording differ: this one is a real submission the
 * write path should have refused.
 */
export function isNoOpDateProposal(line: ProposalFields): boolean {
  if (!isChangeProposedStatus(line.acknowledge_status)) return false;
  if (line.supplier_proposed_qty || line.supplier_proposed_unit_price) return false;
  const to = line.supplier_proposed_date || '';
  const from = line.original_promise_date || line.delivery_date || '';
  return Boolean(to && from && to === from);
}

/**
 * May this line's change be accepted or rejected?
 *
 * The gate for every Accept / Reject control, on the line modal and on the
 * list's bulk actions alike. False for a phantom and for a no-op proposal:
 * in both cases the screen has nothing to show the user, so it must not offer
 * them a button either.
 */
export function canActOnProposal(line: ProposalFields): boolean {
  return (
    isChangeProposedStatus(line.acknowledge_status) &&
    hasProposalPayload(line) &&
    !isNoOpDateProposal(line)
  );
}
